import type { SentimentMode } from 'features/charts-v2/components/SentimentFilterCard'
import {
  type FilterValueSortBy,
  type FilterValueSortDirection,
  sortFilterOptionValues,
} from 'features/charts-v2/utils/sort-filter-option-values'
import type { FilterOptionValue } from 'features/filters/types'

/** The dashboard global's constraint on this pane, split by mode — 'sentiment'
 *  (sentence) and 'post_sentiment' (record) are different backend fields, so
 *  a global constraining one does not constrain the other; restricting only
 *  the active mode's values would not be a subset relation once the user
 *  switches mode. `allowedModes` covers that: undefined = either mode may be
 *  freely selected, otherwise only the listed mode(s) may be newly chosen. */
export type SentimentPaneRestriction = {
  sentence?: ReadonlySet<string>
  record?: ReadonlySet<string>
  allowedModes?: readonly SentimentMode[]
}

/**
 * The sorted, restriction-filtered value list one mode's checkbox list
 * renders. Restricted to the values a global filter allows for the ACTIVE
 * mode's field — restricting 'sentiment' does not restrict 'post_sentiment'
 * (see `SentimentPaneRestriction`'s doc comment), so this re-resolves
 * whenever the user switches Type. Already-selected values stay visible
 * (never silently dropped) so the user can un-select them — same rule as
 * `restrictFilterOptions`.
 */
export function resolveVisibleSentimentValues(
  allValues: FilterOptionValue[],
  draftMode: SentimentMode,
  draftValues: string[],
  sortOptions: {
    sortBy: FilterValueSortBy
    sortDirection: FilterValueSortDirection
  },
  restriction: SentimentPaneRestriction | undefined,
): FilterOptionValue[] {
  const allowedValuesForMode =
    draftMode === 'record' ? restriction?.record : restriction?.sentence

  return sortFilterOptionValues(
    allValues,
    (v) => draftValues.includes(v.value),
    sortOptions,
  ).filter(
    (v) =>
      !allowedValuesForMode ||
      allowedValuesForMode.has(v.value) ||
      draftValues.includes(v.value),
  )
}
